
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnDestroy, ViewChildren, QueryList, AfterViewInit } from '@angular/core';
import { OnInit } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

import { catchError, combineLatest, EMPTY, finalize, mergeMap, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { FilterService, MessageService } from 'primeng/api';

import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { ContentLibrarySearchService } from '../../../../Common/ContentLibrary/services/content-library-search.service';
import { ContentLibraryFolderModel } from '../../models/content-library-folder-model';
import { StorageSettingsModel } from '../../../../Common/ContentLibrary/models/storage-settings.model';
import { MediaSummaryModel } from '../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { MediaServiceAccessTypeEnum } from '../../../../api/mediaService/enums/media-service-access-type.enum';
import { DefaultFoldersEnum } from '../../../../Common/ContentLibrary/enums/default-folder.enum';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { ContentLibraryFolderService } from '../../services/content-library-folder.service';
import { ContentLibraryMediaService } from '../../services/content-library-media.service';
import { ActionsEnum } from '../../../../Common/Basic/enums/actions.enum';
import { LibraryMoveDialogComponent } from '../../../../Common/Dialogs/components/LibrarySelectionDialog/library-selection-dialog.component';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { MediaModel } from '../../../../Common/ContentLibrary/models/content-library-media.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { UserNoteModel } from '../../../../Common/Misc/models/user-note.model';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { MediaServiceErrorCodesEnum, MediaResultModel } from '../../../../api/mediaService/mediaResults.api.model';
import { ContentLibraryPopupComponent } from '../ContentLibraryPopup/content-library-popup.component';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { sortArrayByString } from '../../../../Common/Misc/helpers/sort/sort.helper';
import { UpdateLibraryContentRequestApi } from '../../../../api/contentLibrary/models/content-library-media.api.model';
import { ContentLibraryFolderApiService } from '@api/contentLibrary/content-library-folder.api.service';
import { LibraryItemDetailsComponent } from '../LibraryItemDetails/library-item-details.component';

@Component({
    selector: 'agpr-manage-content-library-dialog',
    templateUrl: './manage-content-library-dialog.html',
    styleUrls: ['./manage-content-library-dialog.scss'],
    providers: [
        {
            provide: 'SortOrderServiceInterface',
            useExisting: ContentLibrarySearchService,
        },
    ],
    standalone: false
})

export class ManageContentLibraryDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    public readonly buttonTypes = ButtonTypesEnum;
    readonly defaultFolders = DefaultFoldersEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() currentUser: UserModel;
    @Output() clickOutside = new EventEmitter<void>();
    @Input() singleSelect: boolean = false;
    @Input() useAcceptedFilesTypes: boolean = false;
    @Input() maxFiles: number = 0;
    @Input() navBarLaunch: boolean = true;

    @Input() acceptedFileTypes: string[] = ['image/png', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/tiff', 'image/gif'];

    @ViewChild(CdkVirtualScrollViewport) virtualScrollBody: CdkVirtualScrollViewport;
    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
    @ViewChild('libraryItemDetails') libraryItemDetails: LibraryItemDetailsComponent;
    @ViewChildren('newFolder') newFolderRefs: QueryList<ElementRef>;
    @ViewChildren('newMediaItem') mediaItemRefs: QueryList<ElementRef>;

    hideSlideOverlay: boolean = false;
    selectedItem: MediaModel;
    mediaSummary?: MediaModel;
    itemDetails: MediaModel;
    userNotes: UserNoteModel[] = [];
    currentlySelectedItems: MediaSummaryModel[] = [];
    storageSettings: StorageSettingsModel;
    foldersList: ContentLibraryFolderModel[] = [];
    displayMediaItems: MediaSummaryModel[] = [];
    displayMediaItemsRows: [] = [];
    allContentFolder: ContentLibraryFolderModel = { name: this.defaultFolders.ALL_CONTENT } as ContentLibraryFolderModel;
    uncategorizedFolder: ContentLibraryFolderModel = { name: this.defaultFolders.UNCATEGORIZED } as ContentLibraryFolderModel;
    editingFolderId: number;
    editedFolderName: string = '';
    totalStorageBytes: number;
    usedStorageBytes: number;
    selectedItemId: string | number = null;
    fileUploading: boolean = false;
    selectedTabIndex: number = 0;
    noContainerWhenEmpty: boolean = true;
    newlyCreatedItemId: string;
    newlyCreatedFolderName: string;

    contentLibraryActions = [
        {
            label: 'Delete',
            command: () => {
                this.bulkItemAction(ActionsEnum.delete);
            }
        },
        {
            label: 'Move',
            command: () => {
                this.bulkItemAction(ActionsEnum.move);
            }
        }
    ];

    selectedFolderType: DefaultFoldersEnum = this.defaultFolders.ALL_CONTENT;

    selectedFolder: ContentLibraryFolderModel;
    private subscriptions: Subscription[] = [];
    currentDisplayItems: MediaSummaryModel[] = [];
    isAllSelected: boolean = false;

    constructor(
        public coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        public filterService: FilterService,
        private contentLibrarySearchService: ContentLibrarySearchService,
        private mediaServiceApi: MediaServiceApi,
        private contentLibraryFolderService: ContentLibraryFolderService,
        private contentLibraryMediaService: ContentLibraryMediaService,
        private currentUserService: CurrentUserStateService,
        private usersService: UsersService,
        private contentLibraryFolderApiService: ContentLibraryFolderApiService
    ) {
        super(coreDialogService, 'Managecontentlibrary');
    }

    ngOnInit() {
        this.selectFolder(this.allContentFolder);
        this.contentLibrarySearchService.setSortField(this.contentLibrarySearchService.sortFields[0]);
        this.contentLibrarySearchService.setSortOrder(SortOrderEnum.ascending);

        this.subscriptions.push(
            combineLatest([
                this.contentLibrarySearchService.libraryFolders$,
                this.contentLibraryMediaService.all$
            ]).subscribe(([folders, mediaItems]) => {
                // Update folders
                sortArrayByString(folders, 'name', SortOrderEnum.ascending);
                this.foldersList = [...folders];
                if (mediaItems) {
                    // Update item counts
                    this.allContentFolder.count = mediaItems.length;
                    this.uncategorizedFolder.count = mediaItems.filter(item => !item.libraryIds?.length).length;

                    this.foldersList?.forEach(folder => {
                        folder.count = mediaItems.filter(item =>
                            item.libraryIds?.includes(folder.id)
                        ).length;
                    });
                }
            }),
            this.currentUserService.getUser().subscribe(x => {
                this.currentUser = x;
            }),
            this.contentLibrarySearchService.displayMediaItems$$.subscribe(items => {
                this.contentLibrarySearchService.handleSort();
                this.setDisplayMediaItems(items?.slice() || []);
                this.scrollToNewMediaItem();
            }),
            this.contentLibrarySearchService.storageSettings$.subscribe(
                (storageSettings: StorageSettingsModel) => {
                    this.totalStorageBytes = storageSettings.contentStorageLimitBytes * 1024;
                    this.usedStorageBytes = Math.round((storageSettings.contentUsedBytes || 0) * 1024 * 1000) / 1000;
                }
            )
        );
    }

    ngAfterViewInit() {
        this.newFolderRefs.changes.subscribe(() => {
            requestAnimationFrame(() => {
                this.scrollToNewFolder();
            });
        });
        this.mediaItemRefs.changes.subscribe(() => {
            this.scrollToNewMediaItem();
        });
    }

    isSelected(item: MediaSummaryModel): boolean {
        return this.displayMediaItems.some(selected => selected.id === item.id);
    }

    onSelectionChange(item: MediaSummaryModel): void {
        if (this.singleSelect) {
            this.currentlySelectedItems.forEach(i => {
                if (i.id !== item.id) {
                    i.isSelected = false;
                }
            });
            this.currentlySelectedItems = [];

            if (item.isSelected) {
                this.currentlySelectedItems = [item];
            }
            // if no item is selected, select the checked item
            if (!this.selectedItemId) {
                this.onItemClicked(item as unknown as MediaModel);
            }
        } else {
            if (!item.isSelected) {
                this.currentlySelectedItems = this.currentlySelectedItems?.filter(i => i.id !== item.id);
            } else {
                this.currentlySelectedItems.push(item);
            }
            // if no item is selected, select checked item
            if (!this.selectedItemId) {
                this.onItemClicked(item as unknown as MediaModel);
            }
        }

        if (this.currentlySelectedItems.length > 0) {
            this.selectedTabIndex = 1;
        } else {
            this.selectedTabIndex = 0;
        }
    }

    bulkItemAction(action: ActionsEnum) {
        if (action === ActionsEnum.delete) {
            this.coreDialogService.open(
                ConfirmDialogComponent,
                <ConfirmDialogModel>{
                    inputs: {
                        id: 'manageContentLibraryDialog_deleteFiles',
                        heading: 'Common.lhp.libraryMoveDialog.bulk.action.delete.heading',
                        okLabel: 'Common.lhp.libraryMoveDialog.bulk.action.delete.label',
                        alertMessage: 'Common.lhp.libraryMoveDialog.bulk.action.delete.warning',
                        alertType: messageSeverityEnum.warning,
                        message: this.localizationService.transform(
                            'Common.lhp.libraryMoveDialog.bulk.action.delete.message', { selectedItemsCount: this.currentlySelectedItems.length }),
                        confirmAction: () => {
                            const isSelectedItemBeingDeleted = this.currentlySelectedItems.some(item => item.id === this.selectedItemId);
                            const deleteObservables = this.currentlySelectedItems.map(item =>
                                this.mediaServiceApi.delete(item.id)
                            );
                            if (isSelectedItemBeingDeleted) {
                                this.onItemClicked(null);
                            }
                            combineLatest(deleteObservables).pipe(
                                tap(() => {
                                    this.messageService.add({
                                        severity: messageSeverityEnum.success,
                                        summary: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.success.label'),
                                        detail: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.delete.success.message')
                                    });
                                    this.refreshAll();
                                    this.onClearSelection();
                                }),
                                catchError((error) => {
                                    this.messageService.add({
                                        severity: messageSeverityEnum.error,
                                        summary: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.delete.error.label'),
                                        detail: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.delete.error.message')
                                    });
                                    throw error;
                                })
                            ).subscribe();
                            return this.mediaServiceApi.delete(this.currentlySelectedItems[0].id);
                        }
                    },
                    outputs: {
                        dialogClose: () => { }
                    }
                }
            );
        } else if (action === ActionsEnum.move) {
            const io: DialogIoModel = {
                inputs: {
                    heading: 'Common.lhp.libraryMoveDialog.bulk.action.move.heading',
                    selectedItemsCount: this.currentlySelectedItems.length,
                    groupOptions: this.contentLibraryFolderService.all$,
                },
                outputs: {
                    dialogClose: (selectedLibrary: ContentLibraryFolderModel) => {
                        if (selectedLibrary) {
                            const mediaIds = this.currentlySelectedItems.map(item => item.id);
                            this.contentLibraryFolderService.moveToLibrary(selectedLibrary.id.toString(), mediaIds).pipe(
                                tap(() => {
                                    this.messageService.add({
                                        severity: messageSeverityEnum.success,
                                        summary: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.success.label'),
                                        detail: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.move.success.message')
                                    });
                                    this.refreshAll();
                                    this.onClearSelection();
                                }),
                                catchError((error) => {
                                    this.messageService.add({
                                        severity: messageSeverityEnum.error,
                                        summary: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.move.error.label'),
                                        detail: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.move.error.message')
                                    });
                                    throw error;
                                })
                            ).subscribe();
                        }
                    },
                    dialogCancelled: () => {
                    }
                }
            };
            this.coreDialogService.open(LibraryMoveDialogComponent, io);
        }
    }

    onClearSelection(): void {
        for (const item of this.currentlySelectedItems) {
            item.isSelected = false;
        }
        this.currentlySelectedItems = [];
        this.selectedTabIndex = 0;
    }

    tabIndexChange(index: number) {
        this.selectedTabIndex = index;
    }

    onItemClicked(item: MediaModel): void {
        this.selectedItem = item;
        this.selectedTabIndex = 0;
        this.selectedItemId = item?.id;
        if (this.selectedItem?.id) {
            this.loadMediaModel(this.selectedItem.id);
        }
        if (this.selectedItem?.createdByUserId) {
            this.usersService.getUser(item.createdByUserId).subscribe(user => {
                item.uploadedBy = user.getDisplayName();
            });
        }
    }

    loadMediaModel(contentId: string) {
        if (!contentId) {
            return;
        }

        this.contentLibraryMediaService.getMediaModel(contentId).subscribe((media: MediaModel) => {
            this.mediaSummary = media;
            this.userNotes = media?.notes || [];
        });
    }

    onIconHover(event: MouseEvent): void {
        event.stopPropagation();
        const element = document.getElementById('contentLibraryIcon');
        const rect = element.getBoundingClientRect();
        const containerWidth = 370;
        const io = {
            inputs: {
                // for dialog to be centered with the info icon
                positionX: rect.left - containerWidth / 2 + rect.width / 2,
                positionY: rect.bottom,
                containerWidth: containerWidth
            },
            outputs: {}
        };

        this.coreDialogService.open(ContentLibraryPopupComponent, io);
    }

    selectFolder(folder: ContentLibraryFolderModel): void {
        this.selectedFolder = folder;
        this.selectedFolderType = this.defaultFolders.ALL_CONTENT;
        this.selectedItem = null;
        this.contentLibraryMediaService.all$.pipe(
            take(1),
            tap(() => this.contentLibrarySearchService.setSelectedFolder(folder))
        ).subscribe();
    }

    setDisplayMediaItems(items: MediaSummaryModel[]): void {
        this.displayMediaItems = items;
        this.displayMediaItemsRows = [].constructor(Math.ceil(this.displayMediaItems.length / 2));
    }

    cancel(): void {
        this.onClearSelection();
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    onUploadClick() {
        this.fileInput.nativeElement.click();
    }

    onFileSelect(): void {
        if (this.currentlySelectedItems?.length) {
            if (this.useAcceptedFilesTypes) {
                const doesNotMatch = this.currentlySelectedItems.find(mediaModel => !this.acceptedFileTypes.includes(mediaModel.mimeType));
                if (doesNotMatch) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('PageBuilder.error.fileTypeHeader'),
                        detail: this.localizationService.transform('PageBuilder.error.fileTypeMessage', {
                            types: this.acceptedFileTypes.map(type => type.replace('image/', '')).join(', ')
                        })
                    });
                    return;
                }
            }

            if (this.maxFiles && (this.currentlySelectedItems.length > this.maxFiles)) {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.forms.fileUpload.error.tooManyFiles.summary'),
                    detail: this.localizationService.transform(
                        'Common.forms.fileUpload.error.tooManyFiles.detail',
                        { files: this.maxFiles }
                    )
                });
                return;
            }

            const selectedItems = [...this.currentlySelectedItems];
            this.displayMediaItems = this.displayMediaItems.map(item => {
                item.isSelected = false;
                return item;
            });
            this.currentlySelectedItems = [];
            this.dialogClose.emit(selectedItems);
            this.closeDialog();
        }
    }

    refreshAll() {
        this.contentLibraryMediaService.refreshAll();
        this.contentLibrarySearchService.refreshUsedStorage();
    }

    scrollToNewFolder() {
        if (this.newlyCreatedFolderName) {
            const folderRef = this.newFolderRefs?.find(ref => {
                const content = ref.nativeElement.querySelector('.agpr-column-content-folders-item-label-name')?.textContent?.trim();
                return content === this.newlyCreatedFolderName;
            });
            if (folderRef) {
                folderRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                this.newlyCreatedFolderName = '';
            }
        }
    }

    scrollToNewMediaItem() {
        if (this.newlyCreatedItemId) {
            const index = this.displayMediaItems.findIndex(item => item?.id === this.newlyCreatedItemId);
            if (index >= 0) {
                const rowIndex = Math.floor(index / 2);
                const viewport = document.querySelector('cdk-virtual-scroll-viewport');
                viewport?.scrollTo({ top: rowIndex * 86, behavior: 'smooth' });
                if (!this.selectedItemId) {
                    const newItem = this.displayMediaItems.find(item => item?.id === this.newlyCreatedItemId);
                    if (newItem) {
                        this.onItemClicked(newItem as unknown as MediaModel);
                    }
                }
                this.newlyCreatedItemId = null;
            }
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = input.files;

        if (!files?.length) {
            return;
        }

        this.fileUploading = true;

        this.mediaServiceApi.upload([files[0]], MediaServiceAccessTypeEnum.PUBLIC).pipe(
            switchMap(response => {
                const mediaResult = response.mediaResults?.[0];

                if (mediaResult?.codedError) {
                    this.addUploadErrorMessage(files[0].name, mediaResult.codedError);
                    return EMPTY;
                }

                const fileList$ = this.selectedFolder?.id
                    ? this.contentLibraryMediaService.getMediaItemsByFolderId(this.selectedFolder?.id)
                    : this.contentLibraryMediaService.all$.pipe(take(1));

                return combineLatest([
                    fileList$.pipe(take(1)), of(mediaResult)
                ]).pipe(take(1));
            }),
            switchMap(([existingfileList, file]: [MediaSummaryModel[], MediaResultModel]) => {
                const fileId = file.media.id;
                const duplicate = existingfileList.find(item => item?.id === fileId);

                this.newlyCreatedItemId = fileId;

                if (duplicate && this.selectedFolder === this.uncategorizedFolder) {
                    this.selectFolder(this.allContentFolder);
                }

                if (!this.selectedItemId) {
                    const newItem = this.displayMediaItems.find(item => item?.id === fileId);
                    if (newItem) {
                        this.onItemClicked(newItem as unknown as MediaModel);
                    }
                }

                if (duplicate) {
                    this.addUploadErrorMessage(files[0].name, {code: MediaServiceErrorCodesEnum.duplicateFile});
                    return EMPTY;
                }

                return of(file);
            }),
            mergeMap(file => {
                const mediaResult = file;
                if (this.selectedFolder?.id) {
                    return this.contentLibraryFolderService.moveToLibrary(
                        this.selectedFolder.id.toString(),
                        [mediaResult.media.id]
                    );
                }
                return of(null);
            }),
            finalize(() => {
                input.value = '';
                this.fileUploading = false;
            })
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.success.label'),
                    detail: this.localizationService.transform('Common.lhp.libraryMoveDialog.bulk.action.upload.success.message')
                });
                this.refreshAll();
            },
            error: (error) => {
                this.fileUploading = false;
                this.addUploadErrorMessage(files[0].name, error);
            },
        });
    }

    addUploadErrorMessage(resultName, error) {
        let summaryMessage;
        let detailMessage;

        switch (error.code) {
            case MediaServiceErrorCodesEnum.invalidType:
                summaryMessage = 'Common.mediaService.fileUpload.typeError.heading';
                detailMessage = 'Common.mediaService.fileUpload.typeError.message';
                break;
            case MediaServiceErrorCodesEnum.fileMaxSize:
                summaryMessage = 'Common.mediaService.fileUpload.maxSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.maxSize.message';
                break;
            case MediaServiceErrorCodesEnum.insufficientSpace:
                summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                break;
            case MediaServiceErrorCodesEnum.fileMinSize:
                summaryMessage = 'Common.mediaService.fileUpload.minSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.minSize.message';
                break;
            case MediaServiceErrorCodesEnum.duplicateFile:
                summaryMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.label';
                detailMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.message';
                break;
            case MediaServiceErrorCodesEnum.internalError:
            default:
                if (error.status === 429) {
                    summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                    detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                } else {
                    summaryMessage = 'Common.mediaService.fileUpload.internalError.heading';
                    detailMessage = 'Common.mediaService.fileUpload.internalError.message';
                }
                break;
        }

        this.messageService.add({
            severity : messageSeverityEnum.error,
            summary: this.localizationService.transform(summaryMessage),
            detail: this.localizationService.transform(detailMessage, { name: resultName })
        });
    }

    addFolder(): void {
        const folder = new ContentLibraryFolderModel();
        let counter = 1;
        let libraryName = this.localizationService.transform('Common.contentLibrary.create.library', { counter });

        while (this.foldersList.some(f => f.name === libraryName)) {
            counter++;
            libraryName = this.localizationService.transform('Common.contentLibrary.create.library', { counter });
        }

        folder.name = libraryName;
        this.contentLibraryFolderService.createResource(folder).pipe(
            tap(() => {
                this.newlyCreatedFolderName = folder.name;
                this.contentLibraryFolderService.refreshAll();
            })
        ).subscribe();
    }

    deleteFolder(folder: ContentLibraryFolderModel): void {
        this.coreDialogService.open(
            ConfirmDialogComponent,
            <ConfirmDialogModel>{
                inputs: {
                    id: 'manageContentLibraryDialog_deleteLibrary',
                    heading: this.localizationService.transform('Common.contentLibrary.manageDialog.library.deleting'),
                    okLabel: this.localizationService.transform('Common.contentLibrary.manageDialog.library.delete.label'),
                    messageHtml: {
                        key: this.localizationService.transform('Common.contentLibrary.manageDialog.library.delete.message', { name: folder.name }),
                    },
                    confirmAction: () => {
                        return this.contentLibraryMediaService.all$.pipe(
                            take(1),
                            switchMap(mediaItems => {
                                const mediaItemsInFolder = mediaItems.filter(mediaItem =>
                                    mediaItem.libraryIds?.includes(folder.id)
                                );
                                const mediaIds = mediaItemsInFolder.map(item => item.id);
                                if (mediaIds.length === 0) {
                                    return this.contentLibraryFolderService.deleteResource(folder.id);
                                }
                                const request: UpdateLibraryContentRequestApi = {
                                    saveToLibraryContentIds: [],
                                    removeFromLibraryContentIds: mediaIds
                                };
                                return this.contentLibraryFolderApiService.updateLibraryContent(folder.id.toString(), request).pipe(
                                    switchMap(() => this.contentLibraryFolderService.deleteResource(folder.id))
                                );
                            }),
                            tap(() => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Common.contentLibrary.manageDialog.library.Success'),
                                    detail: this.localizationService.transform('Common.contentLibrary.manageDialog.library.Success.message')
                                });
                                this.contentLibraryFolderService.refreshAll();
                                this.contentLibraryMediaService.refreshAll();
                                this.onClearSelection();
                            }),
                            catchError((error) => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: this.localizationService.transform( 'Common.contentLibrary.manageDialog.library.delete.error'),
                                    detail: this.localizationService.transform('Common.contentLibrary.manageDialog.library.delete.error.message')
                                });
                                throw error;
                            })
                        );
                    }
                },
                outputs: {
                    dialogClose: () => { }
                }
            }
        );
    }

    editFolder(folder: ContentLibraryFolderModel): void {
        this.editingFolderId = folder.id;
        this.editedFolderName = folder.name;
    }

    saveFolder(folder: ContentLibraryFolderModel): void {
        if (!this.editedFolderName?.trim()) {
            this.editedFolderName = folder.name;
            this.editingFolderId = null;
            return;
        }

        const folderNameExists = this.foldersList.some(f =>
            f.name.toLowerCase() === this.editedFolderName.toLowerCase() && f.id !== folder.id
        );

        if (folderNameExists) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Common.contentLibrary.manageDialog.library.renaming',
                detail: 'Common.contentLibrary.manageDialog.library.renaming.message'
            });
            this.editedFolderName = folder.name;
        }

        folder.name = this.editedFolderName;
        this.contentLibraryFolderService.updateResource(folder).subscribe(x => {
            this.editingFolderId = null;
            sortArrayByString(this.foldersList, 'name', SortOrderEnum.ascending);
            this.newlyCreatedFolderName = folder.name;
        });
    };

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
